import React, { createContext, useContext, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import api from "../services/api";
import validateTotals from "../common/Documents/utils/validateTotals";

const RecognitionContext = createContext(null);

export const useRecognition = () => {
  const ctx = useContext(RecognitionContext);
  if (!ctx) throw new Error("useRecognition must be used within RecognitionProvider");
  return ctx;
};

const INITIAL_BATCH_STATE = {
  isRunning: false,
  isDone: false,
  total: 0,
  current: 0,
  successCount: 0,
  failCount: 0,
  cancelled: false,
};

export const RecognitionProvider = ({ children }) => {
  const [batchState, setBatchState] = useState(INITIAL_BATCH_STATE);
  const [processingIds, setProcessingIds] = useState(() => new Set());
  // docOverlays: Map<id, mergedDoc> — shared so DocumentPage can pick up live updates
  const [docOverlays, setDocOverlays] = useState(() => new Map());

  // Recognition options saved so the progress panel can display them
  const [batchOptions, setBatchOptions] = useState({ useCompanyCode: true, useMarkdown: false });

  // Refs for cancel and in-progress tracking (survive re-renders without closures)
  const cancelRef = useRef(false);
  const isRunningRef = useRef(false);

  const markProcessing = useCallback((id, on = true) => {
    setProcessingIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  /**
   * Start a batch recognition run.
   * ids            – array of document IDs
   * options        – { useCompanyCode, useMarkdown }
   * onDocComplete  – optional callback(id, success, mergedDoc) for per-doc hooks (used by DocumentPage for overlay)
   */
  const startBatch = useCallback(
    async (ids, options = {}) => {
      if (isRunningRef.current) return; // prevent double-start
      const { useCompanyCode = true, useMarkdown = false } = options;

      cancelRef.current = false;
      isRunningRef.current = true;

      setBatchOptions({ useCompanyCode, useMarkdown });
      setBatchState({
        isRunning: true,
        isDone: false,
        total: ids.length,
        current: 0,
        successCount: 0,
        failCount: 0,
        cancelled: false,
      });

      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < ids.length; i++) {
        if (cancelRef.current) break;

        const id = ids[i];
        markProcessing(id, true);

        // Update progress counter
        setBatchState((prev) => ({ ...prev, current: i + 1 }));

        try {
          await api.post(
            `/api/recognize/`,
            { document_ids: [id], use_company_code: useCompanyCode, use_markdown: useMarkdown },
            { timeout: 300000 }
          );
          successCount++;

          // Fetch fresh document for immediate UI overlay
          try {
            const detailResp = await api.get(`/api/documents/${id}/`);
            const fresh = detailResp.data;
            const { id: dataId, ...dataWithoutId } = fresh.data || {};
            const mergedDoc = {
              ...fresh,
              ...dataWithoutId,
              is_recognized: fresh.is_recognized === true,
              isValid: fresh.data
                ? validateTotals({ ...fresh.data, line_items: fresh.line_items })
                : false,
            };
            setDocOverlays((prev) => {
              const next = new Map(prev);
              next.set(id, mergedDoc);
              return next;
            });
          } catch {
            // Non-fatal
          }
        } catch {
          failCount++;
        } finally {
          markProcessing(id, false);
          setBatchState((prev) => ({
            ...prev,
            successCount,
            failCount,
          }));
        }
      }

      isRunningRef.current = false;
      setBatchState((prev) => ({
        ...prev,
        isRunning: false,
        isDone: true,
        successCount,
        failCount,
        cancelled: cancelRef.current,
      }));
    },
    [markProcessing]
  );

  /**
   * Request cancellation of the current batch.
   */
  const cancelBatch = useCallback(async () => {
    cancelRef.current = true;
    setBatchState((prev) => ({ ...prev, cancelled: true }));
    try {
      await api.post(`/cancel-all-recognitions/`);
    } catch {
      // best-effort
    }
  }, []);

  /**
   * Clear overlays when company / filters change so stale data doesn't bleed through.
   */
  const clearOverlays = useCallback(() => {
    setDocOverlays(new Map());
  }, []);

  /**
   * Dismiss the "done" banner.
   */
  const dismissDone = useCallback(() => {
    setBatchState(INITIAL_BATCH_STATE);
  }, []);

  return (
    <RecognitionContext.Provider
      value={{
        batchState,
        batchOptions,
        processingIds,
        docOverlays,
        startBatch,
        cancelBatch,
        clearOverlays,
        dismissDone,
      }}
    >
      {children}
    </RecognitionContext.Provider>
  );
};

RecognitionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default RecognitionContext;
